// Local history of finished computer games (plan §9: bot games are fully
// client-side). We keep them in localStorage rather than the server — there's no
// opponent account and no rating at stake — so they can be listed on the home
// screen and re-opened on a passive replay board (with analysis available as an
// explicit option). Best-effort: storage may be unavailable (private mode) or
// full, in which case these degrade to no-ops.

import type { Color, MoveHist } from "./types.ts";

export interface BotGameRecord {
  id: string;
  createdAt: number;
  level: number;
  humanColor: Color;
  tc: string; // "5+3" or "unlimited"
  status: string; // mate | timeout | resign | stalemate | insufficient | threefold | fifty | draw
  winner: Color | null;
  result: "won" | "lost" | "draw"; // from the human's perspective
  moves: MoveHist[];
}

const KEY = "chess.botGames";
const MAX = 24;

export function listBotGames(): BotGameRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as BotGameRecord[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function getBotGame(id: string): BotGameRecord | undefined {
  return listBotGames().find((g) => g.id === id);
}

/** Wipe the local computer-game history (used by the superuser DB reset, which
 *  otherwise leaves this client-side history in place). Best-effort. */
export function clearBotGames(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Persist a finished game (newest first, capped) and return its id. */
export function saveBotGame(rec: Omit<BotGameRecord, "id" | "createdAt">): string {
  const id = `bot_${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
  const full: BotGameRecord = { ...rec, id, createdAt: Date.now() };
  try {
    const all = [full, ...listBotGames()].slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* ignore: history is a convenience, not load-bearing */
  }
  return id;
}
